import {
  EmployeeShiftOffer,
  ShiftOfferStatus,
} from '../../employee_shift/models/employeeShiftOffer'
import { Employee } from '../../employees/models/employee'
import { Shift } from '../../shifts/models/shift'

export const SHIFT_OFFER_ACTION_TYPE = {
  VIEW_EMPLOYEE: 'view_employee',
  ASSIGN: 'assign',
  REMOVE: 'remove',
  APPROVE: 'approve',
  REJECT: 'reject',
  CANCEL: 'cancel',
  SET_APPLIED: 'set_applied',
  SET_PENDING: 'set_pending',
  SET_REJECTED: 'set_rejected',
} as const
export type ShiftOfferActionType =
  (typeof SHIFT_OFFER_ACTION_TYPE)[keyof typeof SHIFT_OFFER_ACTION_TYPE]

export interface ShiftOfferActionContext {
  shift: Shift
  employee: Employee
  offer: EmployeeShiftOffer
}

export interface ShiftOfferActionDefinition {
  type: ShiftOfferActionType
  order: number
  label: string
  icon?: string
  severity?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warn'
    | 'help'
    | 'danger'
    | 'contrast'
  group?: string
  isMain?: boolean
  requiredStatus?: ShiftOfferStatus[]
  setStatus?: ShiftOfferStatus
  isAvailable?: (_context: ShiftOfferActionContext) => boolean
  handler: (_context: ShiftOfferActionContext) => void
  requiresConfirmation?: boolean
  confirmationMessage?: string | ((_context: ShiftOfferActionContext) => string)
}

export const ViewEmployeeActionDefinition = {
  type: SHIFT_OFFER_ACTION_TYPE.VIEW_EMPLOYEE,
  label: 'View Employee',
  order: 10,
  handler: (_context: ShiftOfferActionContext) => {
    /* Implement navigation to employee details */
  },
} as ShiftOfferActionDefinition

export const AssignActionDefinition = {
  type: SHIFT_OFFER_ACTION_TYPE.ASSIGN,
  order: 20,
  label: 'Assign to Work',
  setStatus: 'accepted',
  requiredStatus: [
    'pending',
    'queued',
    'declined',
    'rejected',
  ] as const as ShiftOfferStatus[],
  isAvailable: (_context: ShiftOfferActionContext) => {
    return (
      _context.shift.actions?.includes('shift.edit') &&
      !_context.shift.rosterEmployeeId
    )
  },
  handler: (_context: ShiftOfferActionContext) => {
    /* Implement shift assignment logic */
  },
} as ShiftOfferActionDefinition

export const RemoveActionDefinition = {
  type: SHIFT_OFFER_ACTION_TYPE.REMOVE,
  order: 100,
  label: 'Remove',
  isAvailable: (_context: ShiftOfferActionContext) => {
    return (
      _context.shift.actions?.includes('shift.edit') && !_context.shift.open
    )
  },
  handler: (_context: ShiftOfferActionContext) => {
    /* Implement shift removal logic */
  },
  requiresConfirmation: true,
  confirmationMessage: (_context: ShiftOfferActionContext) =>
    `Remove ${_context.employee.displayName}? They will no longer be able to view or respond to this shift.`,
} as ShiftOfferActionDefinition

export const ApproveActionDefinition = {
  type: SHIFT_OFFER_ACTION_TYPE.APPROVE,
  order: 1,
  label: 'Approve',
  icon: 'pi pi-check',
  severity: 'success',
  isMain: true,
  requiredStatus: ['applied'] as const as ShiftOfferStatus[],
  setStatus: 'accepted',
  isAvailable: (_context: ShiftOfferActionContext) => {
    return (
      _context.shift.actions?.includes('shift.edit') &&
      !_context.shift.rosterEmployeeId
    )
  },
  handler: (_context: ShiftOfferActionContext) => {
    /* Implement shift approval logic */
  },
} as ShiftOfferActionDefinition

export const RejectActionDefinition = {
  type: SHIFT_OFFER_ACTION_TYPE.REJECT,
  order: 2,
  label: 'Reject',
  icon: 'pi pi-times',
  severity: 'danger',
  isMain: true,
  requiredStatus: ['applied'] as const as ShiftOfferStatus[],
  setStatus: 'rejected',
  isAvailable: (_context: ShiftOfferActionContext) => {
    return _context.shift.actions?.includes('shift.edit')
  },
  handler: (_context: ShiftOfferActionContext) => {
    /* Implement shift rejection logic */
  },
} as ShiftOfferActionDefinition

export const CancelActionDefinition = {
  type: SHIFT_OFFER_ACTION_TYPE.CANCEL,
  order: 3,
  label: 'Cancel',
  icon: 'pi pi-times',
  severity: 'warn',
  isMain: true,
  requiredStatus: ['accepted'] as const as ShiftOfferStatus[],
  setStatus: 'rejected',
  isAvailable: (_context: ShiftOfferActionContext) => {
    return _context.shift.actions?.includes('shift.edit')
  },
  handler: (_context: ShiftOfferActionContext) => {
    /* Implement shift cancellation logic */
  },
} as ShiftOfferActionDefinition

export const SetAppliedActionDefinition = {
  type: SHIFT_OFFER_ACTION_TYPE.SET_APPLIED,
  order: 30,
  label: 'Set to Applied',
  group: 'Override Status',
  requiredStatus: [
    'accepted',
    'pending',
    'queued',
    'declined',
    'rejected',
  ] as const as ShiftOfferStatus[],
  setStatus: 'applied',
  isAvailable: (_context: ShiftOfferActionContext) => {
    return (
      _context.shift.actions?.includes('shift.edit') &&
      _context.shift.approvalRequired
    )
  },
  handler: (_context: ShiftOfferActionContext) => {
    /* Implement logic to set offer to applied */
  },
} as ShiftOfferActionDefinition

export const SetPendingActionDefinition = {
  type: SHIFT_OFFER_ACTION_TYPE.SET_PENDING,
  order: 30,
  label: 'Set to Pending',
  group: 'Override Status',
  requiredStatus: [
    'accepted',
    'applied',
    'queued',
    'declined',
    'rejected',
  ] as const as ShiftOfferStatus[],
  setStatus: 'pending',
  isAvailable: (_context: ShiftOfferActionContext) => {
    return _context.shift.actions?.includes('shift.edit')
  },
  handler: (_context: ShiftOfferActionContext) => {
    /* Implement logic to set offer to pending */
  },
} as ShiftOfferActionDefinition

export const SetRejectedActionDefinition = {
  type: SHIFT_OFFER_ACTION_TYPE.SET_REJECTED,
  order: 30,
  label: 'Set to Rejected',
  group: 'Override Status',
  requiredStatus: [
    'accepted',
    'applied',
    'queued',
    'pending',
  ] as const as ShiftOfferStatus[],
  setStatus: 'rejected',
  isAvailable: (_context: ShiftOfferActionContext) => {
    return _context.shift.actions?.includes('shift.edit')
  },
  handler: (_context: ShiftOfferActionContext) => {
    /* Implement logic to set offer to rejected */
  },
} as ShiftOfferActionDefinition

export const shiftOfferActionDefinitions: ShiftOfferActionDefinition[] = [
  ViewEmployeeActionDefinition,
  AssignActionDefinition,
  RemoveActionDefinition,
  ApproveActionDefinition,
  RejectActionDefinition,
  CancelActionDefinition,
  SetAppliedActionDefinition,
  SetPendingActionDefinition,
  SetRejectedActionDefinition,
]
